import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Save, Refresh, ShoppingCart } from '@mui/icons-material';
import { createAdminGroupBuyPool } from '../../services/groupBuyService';

const CreatePool = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    item_id: '',
    max_participants: 50,
    expiration_date: '',
    is_featured: false,
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const required = ['title', 'item_id'];
    const missing = required.filter(field => !formData[field]?.trim());

    if (missing.length > 0) {
      setSnackbar({
        open: true,
        message: `Please fill in: ${missing.join(', ')}`,
        severity: 'error'
      });
      return false;
    }

    if (isNaN(formData.item_id) || parseInt(formData.item_id) <= 0) {
      setSnackbar({
        open: true,
        message: 'Item ID must be a valid number',
        severity: 'error'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        item_id: parseInt(formData.item_id),
        max_participants: parseInt(formData.max_participants),
        expiration_date: formData.expiration_date || null,
      };

      const response = await createAdminGroupBuyPool(submitData);

      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Pool created successfully!',
          severity: 'success'
        });
        // Reset form
        setFormData({
          title: '',
          description: '',
          item_id: '',
          max_participants: 50,
          expiration_date: '',
          is_featured: false,
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to create pool',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating pool:', error);
      setSnackbar({
        open: true,
        message: 'Failed to create pool. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Create Group Buy Pool
        </Typography>
      </Box>

      {/* Form */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pool Information
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={3}>
                Create a new group buying pool. This will be active immediately for users to join.
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Pool Title"
                    value={formData.title}
                    onChange={(e) => updateFormData('title', e.target.value)}
                    placeholder="Enter an attractive title for the pool"
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={formData.description}
                    onChange={(e) => updateFormData('description', e.target.value)}
                    placeholder="Describe the pool and what makes it special"
                    multiline
                    rows={3}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Item ID"
                    type="number"
                    value={formData.item_id}
                    onChange={(e) => updateFormData('item_id', e.target.value)}
                    placeholder="Enter the Taobao item ID"
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Max Participants"
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => updateFormData('max_participants', e.target.value)}
                    placeholder="Maximum number of participants"
                    inputProps={{ min: 2, max: 1000 }}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Expiration Date (Optional)"
                    type="datetime-local"
                    value={formData.expiration_date}
                    onChange={(e) => updateFormData('expiration_date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Featured</InputLabel>
                    <Select
                      value={formData.is_featured}
                      label="Featured"
                      onChange={(e) => updateFormData('is_featured', e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value={false}>No</MenuItem>
                      <MenuItem value={true}>Yes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={() => setFormData({
                        title: '',
                        description: '',
                        item_id: '',
                        max_participants: 50,
                        expiration_date: '',
                        is_featured: false,
                      })}
                      disabled={loading}
                    >
                      Reset
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} /> : <ShoppingCart />}
                      onClick={handleSubmit}
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create Pool'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Instructions
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Admin Pool Creation:</strong> Unlike user pools, admin-created pools don't require an initial purchase.
                  They become active immediately for users to join and purchase.
                </Typography>
              </Alert>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Item ID:</strong> The Taobao item ID that users will purchase when joining this pool.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Max Participants:</strong> Maximum number of users who can join this pool.
              </Typography>

              <Typography variant="body2">
                <strong>Expiration Date:</strong> Optional date when the pool will automatically close.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreatePool;